import { Plural, Trans, useLingui } from '@lingui/react/macro';

import { CardItem } from '@/components/Card/Card';
import Badge from '@/design_system/Badge';
import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import Tooltip from '@/design_system/Tooltip';
import InlineText from '@/design_system/Typography/InlineText';
import IconNoConnection from '@/icons/NoConnection.svg';
import IconTools from '@/icons/Tools.svg';
import { formatCityState } from '@/models/address';
import { getNumberOfActions, useGetArticleName } from '@/models/article';
import { ArticleWithRelations } from '@/models/request';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { useGetExchangeTypes } from '@/utils/exchangeTypes';

export const ArticleServiceTypeCell = ({ article }: { article: ArticleWithRelations }) => {
  const { t } = useLingui();

  const numberOfActions = getNumberOfActions(article);

  const workshopCityState = article.workshop?.address
    ? formatCityState(article.workshop.address)
    : undefined;

  return (
    <Stack style={{ overflow: 'hidden' }}>
      {!article.snapshot.serviceChoice && !article.exchangedFromId && (
        <InlineText weight="medium" color="secondary">
          -
        </InlineText>
      )}
      {article.snapshot.serviceChoice === 'care-repair' && (
        <>
          <InlineText ellipsis>
            <Trans id="request.articles.table.column.service-type.care-and-repair">
              Care and repair
            </Trans>
          </InlineText>
          <InlineText size="200" color="secondary">
            <Trans id="request.articles.table.column.service-type.actions">
              {numberOfActions} <Plural value={numberOfActions} one="action" other="actions" />
            </Trans>
          </InlineText>
          {!!article.workshop && (
            <Stack row gap="1rem" alignItems="center" flexWrap="nowrap">
              <InlineText
                size="200"
                color="secondary"
                ellipsis
                title={`${article.workshop.name}${workshopCityState ? ` • ${workshopCityState}` : ''}`}
              >
                {!!article.workshop.workshopConfig?.nonDigitalized && (
                  <Tooltip
                    content={t({
                      id: 'article-workshop-cell.non-digitalized.tooltip',
                      message: 'Non-digitalized',
                    })}
                    hasOverlayArrow
                  >
                    <Button variant="style-less">
                      <Badge size="x-small" color="secondary" borderRadius="soft" iconOnly>
                        <IconNoConnection />
                      </Badge>
                    </Button>
                  </Tooltip>
                )}
                {!!article.workshop.name && ` ${article.workshop.name}`}
                {!!workshopCityState && ` • ${workshopCityState}`}
                {article.workshopOrganization?.external && (
                  <>
                    {' • '}
                    <Trans id="workshop.external">External</Trans>
                  </>
                )}
              </InlineText>
            </Stack>
          )}
        </>
      )}
      {(article.snapshot.serviceChoice === 'exchange' ||
        (!article.snapshot.serviceChoice && !!article.exchangedFromId)) && (
        <ArticleServiceTypeExchange article={article} />
      )}
      {article.snapshot.serviceChoice === 'exchange-archival' && (
        <InlineText>
          <Trans id="request.articles.table.column.service-type.exchange">Exchange</Trans>
        </InlineText>
      )}
      {article.snapshot.serviceChoice === 'refund' && (
        <InlineText>
          <Trans id="request.articles.table.column.service-type.refund">Refund</Trans>
        </InlineText>
      )}
      {article.snapshot.serviceChoice === 'none' && (
        <InlineText>
          <Trans id="request.articles.table.column.service-type.none">None</Trans>
        </InlineText>
      )}
    </Stack>
  );
};

const ArticleServiceTypeExchange = ({ article }: { article: ArticleWithRelations }) => {
  const getArticleName = useGetArticleName();
  const { exchangeTypeOptions } = useGetExchangeTypes();
  const { request } = useRequestContext();

  const isOriginalArticle = article.snapshot.serviceChoice === 'exchange';

  const originalArticle = isOriginalArticle
    ? article
    : request.allArticles.find((a) => a.id === article.exchangedFromId);
  const replacementArticle = isOriginalArticle
    ? request.allArticles.find((a) => a.exchangedFromId === article.id)
    : article;

  const articleExchange = originalArticle?.snapshot.articleExchange;

  if (!originalArticle || !articleExchange) {
    return (
      <InlineText>
        <Trans id="request.articles.table.column.service-type.exchange">Exchange</Trans>
      </InlineText>
    );
  }

  const exchangeTypeLabel = articleExchange.type
    ? exchangeTypeOptions.find((option) => option.value === articleExchange.type)?.label
    : undefined;

  const originalProductId = originalArticle.productId;
  const replacementProductId = articleExchange.replacementProductId;
  const replacementProductStatus =
    originalProductId && replacementProductId
      ? originalProductId === replacementProductId
        ? 'same'
        : 'different'
      : 'unknown';

  const stockLocationName = articleExchange.stockLocation?.name;

  return (
    <>
      <InlineText>
        {exchangeTypeLabel ? (
          exchangeTypeLabel
        ) : replacementProductStatus === 'different' ? (
          <Trans id="request.articles.table.column.service-type.exchange-different-product">
            Exchange with different product
          </Trans>
        ) : replacementProductStatus === 'same' ? (
          <Trans id="request.articles.table.column.service-type.exchange-same-product">
            Exchange with same product
          </Trans>
        ) : (
          <Trans id="request.articles.table.column.service-type.exchange">Exchange</Trans>
        )}
      </InlineText>

      {!!stockLocationName && (
        <InlineText size="200" color="secondary">
          <Trans id="request.articles.table.column.service-type.stock-location">
            Stock location: {stockLocationName}
          </Trans>
        </InlineText>
      )}

      {!isOriginalArticle && (
        <InlineText size="200" color="secondary">
          <Trans id="request.articles.table.column.service-type.exchanged-from">
            Replacement item for
          </Trans>{' '}
          {getArticleName({ article: originalArticle })}
        </InlineText>
      )}

      {isOriginalArticle && replacementArticle && (
        <InlineText size="200" color="secondary">
          <Trans id="request.articles.table.column.service-type.exchanged-to">
            Exchanged with item
          </Trans>{' '}
          {getArticleName({ article: replacementArticle })}
        </InlineText>
      )}
    </>
  );
};

export const ArticleServiceTypeCardItem = ({ article }: { article: ArticleWithRelations }) => {
  const numberOfActions = getNumberOfActions(article);

  return (
    <CardItem>
      <IconTools style={{ fontSize: '1rem' }} />
      <Trans id="request.articles.table.column.service-type.actions">
        {numberOfActions} {}
        <Plural value={numberOfActions} one="action" other="actions" />
      </Trans>
    </CardItem>
  );
};
